import { ChevronDownIcon, ChevronUpIcon, CloseIcon, GrabHandleIcon, SettingsIcon } from '@arnold/common';
import { useRef, type DragEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { ReportComponent } from '../types';
import { Block, Handle, ToolButton, ToolSeparator, Toolbar, WidthButton } from './editorStyles';
import { hasWidth, type ComponentWidth, type OverviewEditorActions } from './useOverviewEditor';
import { QuestionType } from '../../../../generated/hooks';

const WIDTH_LABELS: Record<ComponentWidth, string> = { full: '1/1', half: '1/2', third: '1/3', quarter: '1/4' };

export function EditableBlock({
  component,
  index,
  count,
  actions,
  dragging,
  onDragStart,
  onDragEnd,
  children,
}: {
  component: ReportComponent;
  index: number;
  count: number;
  actions: OverviewEditorActions;
  dragging: boolean;
  onDragStart: (id: string) => void;
  onDragEnd: () => void;
  children: ReactNode;
}) {
  const { t } = useTranslation('newReport');
  const ref = useRef<HTMLDivElement>(null);
  const configurable = component.type === 'metric' || component.type === 'table';
  const startDrag = (event: DragEvent) => {
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', component.id);
    if (ref.current) event.dataTransfer.setDragImage(ref.current, 24, 24);
    // Chrome cancels the drag when the source re-renders synchronously inside dragstart.
    requestAnimationFrame(() => onDragStart(component.id));
  };
  const configure = () => {
    if (component.type === 'metric') actions.openMetric(component.metricId, { placementId: component.id });
    if (component.type === 'table') actions.setModal({ kind: 'table', component });
  };
  return (
    <Block ref={ref} dragging={dragging} data-component-id={component.id}>
      <Handle
        className="chrome"
        outside={component.type === 'section'}
        type="button"
        draggable
        onDragStart={startDrag}
        onDragEnd={onDragEnd}
        title={t('definition.dragToMove')}
      >
        <GrabHandleIcon />
      </Handle>
      <Toolbar className="chrome">
        {component.type === 'question' && component.questionType === QuestionType.Scale && (
          <ToolButton
            type="button"
            title={t('definition.setMetric')}
            onClick={() => actions.createMetric(index, component.questionIndex)}
          >
            <SettingsIcon />
          </ToolButton>
        )}
        <ToolButton
          type="button"
          disabled={index === 0}
          title={t('definition.moveUp')}
          onClick={() => actions.move(index, index - 1)}
        >
          <ChevronUpIcon />
        </ToolButton>
        <ToolButton
          type="button"
          disabled={index === count - 1}
          title={t('definition.moveDown')}
          onClick={() => actions.move(index, index + 1)}
        >
          <ChevronDownIcon />
        </ToolButton>
        {hasWidth(component) && (
          <>
            <ToolSeparator />
            {(['full', 'half', 'third', 'quarter'] as const).map((width) => (
              <WidthButton
                key={width}
                type="button"
                active={(component.width ?? 'full') === width}
                title={t(`definition.widths.${width}`)}
                onClick={() => actions.updateComponent({ ...component, width })}
              >
                {WIDTH_LABELS[width]}
              </WidthButton>
            ))}
          </>
        )}
        {component.type === 'aiSummary' && (
          <>
            <ToolSeparator />
            {(['collapsible', 'expanded'] as const).map((displayMode) => (
              <WidthButton
                key={displayMode}
                type="button"
                active={(component.displayMode ?? 'collapsible') === displayMode}
                aria-pressed={(component.displayMode ?? 'collapsible') === displayMode}
                title={t(`definition.summaryDisplayModes.${displayMode}Help`)}
                onClick={() => actions.updateComponent({ ...component, displayMode })}
              >
                {t(`definition.summaryDisplayModes.${displayMode}`)}
              </WidthButton>
            ))}
          </>
        )}
        <ToolSeparator />
        {configurable && (
          <ToolButton type="button" title={t('definition.settings')} onClick={configure}>
            <SettingsIcon />
          </ToolButton>
        )}
        <ToolButton
          type="button"
          danger
          title={t('definition.removeComponent')}
          onClick={() => actions.remove(component.id)}
        >
          <CloseIcon />
        </ToolButton>
      </Toolbar>
      {children}
    </Block>
  );
}
